import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import {
  Field,
  TextInput,
  FormSection,
  PrimaryButton,
  GhostButton,
} from '../../components/ui/Field';
import { PasswordStrength } from '../../components/ui/PasswordStrength';
import { Toast } from '../../components/ui/FormField';
import { userApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import { useActivityStore } from '../../stores/activity-store';

// ─────────────────────────────────────────────────────────────
// Change Password — Operations Deck redesign
//
// Centered card layout (the page is form-focused, not list-focused).
// Reuses the shared Field primitives + PasswordStrength meter so the
// validation message and the visible meter never disagree.
// ─────────────────────────────────────────────────────────────

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&]).{8,}$/;

export function ChangePasswordPage() {
  const authUser = useAuthStore((s) => s.user);
  const recordActivity = useActivityStore((s) => s.record);
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Show/hide toggles per field
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  const errors = submitted
    ? {
        currentPassword: !currentPassword ? 'Current password is required' : '',
        newPassword: !newPassword
          ? 'New password is required'
          : !PASSWORD_PATTERN.test(newPassword)
            ? 'Must include 8+ chars, upper, lower, number and special ($@$!%*?&)'
            : newPassword === currentPassword
              ? "New password can't match the current password"
              : '',
        confirmPassword: !confirmPassword
          ? 'Please confirm the new password'
          : newPassword !== confirmPassword
            ? 'Passwords must match'
            : '',
      }
    : { currentPassword: '', newPassword: '', confirmPassword: '' };

  const hasErrors = !!(errors.currentPassword || errors.newPassword || errors.confirmPassword);
  const liveMismatch = !!newPassword && !!confirmPassword && newPassword !== confirmPassword;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setSubmitted(true);
    if (
      !currentPassword
      || !newPassword
      || !confirmPassword
      || !PASSWORD_PATTERN.test(newPassword)
      || newPassword !== confirmPassword
      || newPassword === currentPassword
    ) {
      return;
    }
    setSaving(true);
    try {
      await userApi.changePassword({
        userId: Number(authUser?.userId ?? 0),
        currentPassword,
        newPassword,
      });
      recordActivity({
        type: 'update',
        entity: 'Account',
        label: 'Password',
        user: authUser?.username,
      });
      showToast('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSubmitted(false);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: { errorMessage?: string } } } };
      const msg =
        apiErr?.response?.data?.error?.errorMessage
        ?? 'Failed to change password. Check your current password and try again.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '8px 0 40px',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 560,
          background: 'var(--color-panel)',
          borderRadius: 14,
          border: '1px solid var(--color-border)',
          boxShadow: '0 4px 12px rgba(15,23,32,0.04)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Card header ────────────────────────────────────── */}
        <div
          style={{
            padding: '22px 26px 18px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'rgba(11,74,111,0.10)',
              color: 'var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <KeyRound size={20} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.4,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Account · {authUser?.username ?? '—'}
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 24,
                fontWeight: 500,
                letterSpacing: -0.4,
                color: 'var(--color-text)',
                marginTop: 3,
                marginBottom: 4,
                lineHeight: 1.2,
              }}
            >
              Change password
            </h2>
            <p
              style={{
                fontSize: 13,
                color: 'var(--color-text-mid)',
                margin: 0,
                lineHeight: 1.45,
              }}
            >
              Pick a new password for your account. You'll stay signed in on this device after the change.
            </p>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div
          style={{
            padding: '24px 26px 26px',
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
          }}
        >
          {/* Current password */}
          <FormSection number={1} title="Current password">
            <Field
              label="Current password"
              required
              error={errors.currentPassword || undefined}
            >
              <PasswordInput
                value={currentPassword}
                onChange={setCurrentPassword}
                show={showCurrent}
                onToggleShow={() => setShowCurrent((s) => !s)}
                placeholder="Enter your current password"
                autoComplete="current-password"
                error={!!errors.currentPassword}
              />
            </Field>
          </FormSection>

          {/* New password */}
          <FormSection number={2} title="New password" highlight>
            <Field
              label="New password"
              required
              error={errors.newPassword || undefined}
            >
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                show={showNew}
                onToggleShow={() => setShowNew((s) => !s)}
                placeholder="Choose a strong password"
                autoComplete="new-password"
                error={!!errors.newPassword}
              />
              <PasswordStrength value={newPassword} alwaysShow />
            </Field>
            <Field
              label="Confirm new password"
              required
              error={
                liveMismatch
                  ? 'Passwords must match'
                  : errors.confirmPassword || undefined
              }
            >
              <PasswordInput
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showConfirm}
                onToggleShow={() => setShowConfirm((s) => !s)}
                placeholder="Re-enter the new password"
                autoComplete="new-password"
                error={liveMismatch || !!errors.confirmPassword}
              />
            </Field>
          </FormSection>

          {/* Security tip */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 10,
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
            }}
          >
            <ShieldCheck size={16} style={{ color: 'var(--color-good)', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12.5, color: 'var(--color-text-mid)', lineHeight: 1.5 }}>
              Use a unique password you don't reuse elsewhere. A passphrase of 4 random words is easier
              to remember and harder to guess than a short, complex string.
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 26px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <GhostButton onClick={() => navigate('/dashboard')} disabled={saving}>
            Cancel
          </GhostButton>
          <PrimaryButton type="submit" disabled={saving || hasErrors}>
            {saving ? 'Updating…' : 'Update password'}
          </PrimaryButton>
        </div>
      </form>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PasswordInput — TextInput with an integrated show/hide eye toggle.
// Kept local to this file; promote later if reused.
// ─────────────────────────────────────────────────────────────

function PasswordInput({
  value,
  onChange,
  show,
  onToggleShow,
  placeholder,
  autoComplete,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
  placeholder?: string;
  autoComplete?: string;
  error?: boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        error={error}
        style={{ paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={onToggleShow}
        aria-label={show ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute',
          right: 6,
          top: 5,
          width: 28,
          height: 28,
          borderRadius: 6,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-dim)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}
